import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '../../components/common/Badge/Badge';
import SensitiveStudentBadge from '../../components/common/SensitiveStudentBadge/SensitiveStudentBadge';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import CopyStudentNameButton from '../../components/common/CopyStudentNameButton/CopyStudentNameButton';
import DataTable from '../../components/common/DataTable/DataTable';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import ListingBottomRow from '../../components/common/ListingBottomRow/ListingBottomRow';
import Loading from '../../components/common/Loading/Loading';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import SingleSelectFilter from '../../components/common/SingleSelectFilter/SingleSelectFilter';
import { useSensitiveStudentsDashboard } from '../../hooks/useSensitiveStudentsDashboard';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import { formatDate } from '../../utils/formatting';
import { getBrazilCivilDate } from '../../utils/competence';
import {
  formatSensitivityDuration,
  SENSITIVE_STUDENTS_STATUS_FILTERS,
  SENSITIVE_STUDENTS_STATUS_LABELS,
  type SensitiveStudentsDashboardQuery,
  type SensitiveStudentsStatusFilter,
} from '../../utils/sensitiveStudentsDashboard';
import dashboardStyles from './Dashboard.module.css';
import styles from './SensitiveStudentsPanel.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

const HEADERS = ['Aluna', 'Professora', 'Motivo', 'Tempo de sensibilidade', 'Status'];
const GRID_TEMPLATE = 'minmax(200px, 1.4fr) minmax(140px, 1fr) minmax(180px, 1.3fr) 170px 140px';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  ...SENSITIVE_STUDENTS_STATUS_FILTERS.map((value) => ({
    value,
    label: SENSITIVE_STUDENTS_STATUS_LABELS[value],
  })),
];

const FILTER_SCHEMA = {
  searchName: queryFilter.string(''),
  status: queryFilter.enum<SensitiveStudentsStatusFilter | ''>('', ['', ...SENSITIVE_STUDENTS_STATUS_FILTERS]),
  page: queryFilter.integer(1, { min: 1 }),
  pageSize: queryFilter.integer(20, { allowed: PAGE_SIZE_OPTIONS }),
};

const SensitiveStudentsPanel: React.FC = () => {
  const navigate = useNavigate();
  const { filters, setFilter, resetFilters } = useQueryFilters(
    FILTER_SCHEMA,
    { pageKey: 'page' },
  );

  const query = useMemo<SensitiveStudentsDashboardQuery>(() => ({
    searchName: filters.searchName,
    status: filters.status,
    page: filters.page,
    pageSize: filters.pageSize,
  }), [filters.page, filters.pageSize, filters.searchName, filters.status]);

  const { snapshot, loading, error } = useSensitiveStudentsDashboard(query);
  const [now, setNow] = useState(() => new Date());
  const totalCount = snapshot?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / filters.pageSize));
  const page = Math.min(filters.page, totalPages);
  const students = snapshot?.students ?? [];
  const hasActiveFilters = Boolean(filters.searchName.trim()) || Boolean(filters.status);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className={styles.panel}>
      <FilterBar gap="sm" compact pushFirstChild className={dashboardStyles.filtersRow}>
        <RecordCount label="Alunas:" count={totalCount} />
        <FilterGroup>
          <SearchFilterBar
            searchValue={filters.searchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Nome, professora ou motivo"
            loading={loading}
          />
        </FilterGroup>
        <FilterGroup>
          <SingleSelectFilter
            value={filters.status}
            onChange={(value) => setFilter('status', value as SensitiveStudentsStatusFilter | '')}
            options={STATUS_OPTIONS}
            placeholder="Status"
            neutralValue=""
            keepPlaceholder
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={() => resetFilters()} />
        )}
      </FilterBar>

      {loading && !snapshot && <Loading />}
      {!loading && error && <EmptyState message={error} showImage={false} />}
      {!loading && !error && snapshot && totalCount === 0 && (
        <EmptyState
          message={hasActiveFilters
            ? 'Nenhum período sensível encontrado com os filtros selecionados.'
            : 'Nenhum período sensível registrado.'}
          showImage={false}
        />
      )}
      {!error && snapshot && totalCount > 0 && (
        <>
          <DataTable
            headers={HEADERS}
            gridTemplate={GRID_TEMPLATE}
            lastColumnIsActions={false}
            borderedRows
            boldHeaders
            highlightTerm={filters.searchName}
          >
            {students.map((row) => (
              <DataTable.Row key={row.periodId}>
                <div className={styles['student-cell']}>
                  <div className={styles['name-row']}>
                    <button
                      type="button"
                      className={styles['name-link']}
                      onClick={() => navigate(`/alunas/${row.studentId}/editar`)}
                    >
                      {row.studentName}
                    </button>
                    <CopyStudentNameButton studentName={row.studentName} />
                  </div>
                  {row.ended && row.endedAt ? (
                    <span className={styles['ended-meta']}>
                      Encerrada em {formatDate(getBrazilCivilDate(new Date(row.endedAt)))}
                    </span>
                  ) : null}
                </div>
                <div>{row.coachName || 'Sem professora'}</div>
                <div className={styles['reason-text']}>
                  {row.reason}
                  {row.endJustification ? (
                    <div className={styles['end-justification']}>
                      Justificativa: {row.endJustification}
                    </div>
                  ) : null}
                </div>
                <div className={styles['days-cell']}>
                  {formatSensitivityDuration(row.startedAt, row.endedAt, now)}
                </div>
                <div>
                  {row.ended ? (
                    <Badge variant="primary">Encerrada</Badge>
                  ) : (
                    <SensitiveStudentBadge visible reason={row.reason} />
                  )}
                </div>
              </DataTable.Row>
            ))}
          </DataTable>
          <ListingBottomRow
            pageSize={filters.pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={(event) => {
              const value = Number.parseInt(event.target.value, 10);
              if (PAGE_SIZE_OPTIONS.some((option) => option === value)) {
                setFilter('pageSize', value);
              }
            }}
            pagination={{
              page,
              pageSize: filters.pageSize,
              totalRecords: totalCount,
              totalPages,
              hasNextPage: page < totalPages,
              hasPreviousPage: page > 1,
            }}
            loading={loading}
            onPageChange={(nextPage) => setFilter('page', nextPage, { resetPage: false })}
          />
        </>
      )}
    </section>
  );
};

export default SensitiveStudentsPanel;
